import React, { useEffect, useState } from 'react'
import { FormikProps, withFormik, Form } from 'formik'
import Input from '../../input'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from '../../../helpers/hooks/hookWindowsSize'
import InputTag from '../../inputTag'

interface EditLinksProps {
  onSubmit: (values: EditLinksValues) => void
  children?: React.ReactNode
  data: EditLinksValues
  type: string
  handleClose: (close: boolean) => void
}

interface utmValues {
  origin: string
  media: string
  campaign: string
  term: string
  content: string
}

interface EditLinksValues {
  _id: string
  custom_code: string
  original_url: string
  title: string
  short_url: string
  tags: string[]
  domain: string
  utm: utmValues
}

const InnerForm: React.FunctionComponent<
  EditLinksProps & FormikProps<EditLinksValues>
> = ({
  values,
  handleChange,
  touched,
  data,
  type,
  handleClose,
  setFieldValue,
}) => {
  const { t } = useTranslation()
  const sizeWidth = useWindowSize().width
  const {tags, domain } = data

 const [tagData, setTagData] = useState<string[]>(tags)

  useEffect(() => {
    setFieldValue('tags', tagData)
  }, [tagData])

  const handleCancelClick = () => {
    handleClose(false)
  }

  return (
    <>
      <div className='container-form'>
        <Form>
          <Input
            id='original_url'
            label={`${t('URL')}`}
            type='text'
            value={values.original_url}
            onChange={handleChange}
            touched={touched.original_url}
            classNameInput='input'
            required
            className='col-12'
            classNameLabel='text-align-left color-primary'
          />
          <Input
            id='title'
            label={`${t('TITLE')}`}
            type='text'
            value={values.title}
            onChange={handleChange}
            touched={touched.title}
            classNameInput='input'
            required
            className='col-12'
            classNameLabel='text-align-left color-primary'
          />
          <div className='container-edit-url'>
            <Input
              id='domain'
              label={`${t('DOMAIN')}`}
              type='text'
              value={values.domain || domain}
              onChange={handleChange}
              touched={touched.domain}
              classNameInput='input'
              required
              disabled
              className='col-md-5 col-12'
              classNameLabel='text-align-left'
            />
            {sizeWidth > 767 && (
              <div className='container-link-separator'>
                <span className='link-separator'>/</span>
              </div>
            )}
            <Input
              id='short_url'
              label={`${t('BACK_HALF')}`}
              type='text'
              value={values.short_url}
              onChange={handleChange}
              touched={touched.short_url}
              classNameInput='input'
              required
              className='width-100'
              classNameLabel='text-align-left color-primary'
            />
          </div>
          <div className='container-utm'>
            <span className='sub-title-primary'>{`${t('UTM')}`}</span>
          </div>
          <div className='container-utm'>
            <Input
              id='origin'
              label={`${t('SOURCE')}`}
              type='text'
              value={values.utm.origin}
              onChange={(e) => handleChange('utm.origin')(e)}
              touched={touched.utm?.origin}
              classNameInput='input'
              placeholder={`${t('EG_GOOGLE_NEWSLETTER')}`}
              className='col-12 col-md-4'
              classNameLabel='color-grey-dark'
            />
            <Input
              id='media'
              label={`${t('MEDIUM')}`}
              type='text'
              value={values.utm.media}
              onChange={(e) => handleChange('utm.media')(e)}
              touched={touched.utm?.media}
              classNameInput='input'
              placeholder={`${t('EG_BANNER_EMAIL')}`}
              className='col-12 col-md-4 px-md-4'
              classNameLabel='color-grey-dark'
            />
            <Input
              id='campaign'
              label={`${t('CAMPAIGN')}`}
              type='text'
              value={values.utm.campaign}
              onChange={(e) => handleChange('utm.campaign')(e)}
              touched={touched.utm?.campaign}
              classNameInput='input'
              placeholder={`${t('EG_BLACK_FRIDAY')}`}
              className='col-12 col-md-4'
              classNameLabel='color-grey-dark'
            />
          </div>
          <div className='container-utm'>
            <Input
              id='term'
              label={`${t('TERM')} (${t('OPTIONAL')})`}
              classNameLabel='color-grey-dark'
              type='text'
              value={values.utm.term}
              onChange={(e) => handleChange('utm.term')(e)}
              touched={touched.utm?.term}
              classNameInput='input'
              placeholder={`${t('EG_SOMETHING')}`}
              className='col-12 col-md-4'
            />
            <Input
              id='content'
              label={`${t('CONTENT')} (${t('OPTIONAL')})`}
              classNameLabel='color-grey-dark'
              type='text'
              value={values.utm.content}
              onChange={(e) => handleChange('utm.content')(e)}
              touched={touched.utm?.content}
              classNameInput='input'
              placeholder={`${t('EG_SOMETHING_ELSE')}`}
              className='col-12 col-md-4 px-md-4'
            />
          </div>
          <span className='small'>{`${t('DOMAIN_CANT_BE_CHANGED')}`}</span>
          {type === 'link' && (
            <div className='pt-4'>
              <InputTag
                tags={tags}
                label={`${t('TAGS')}`}
                classNameLabel={'color-primary'}
                onTagDataChange={setTagData}
              />
            </div>
          )}
          <div className='container-buttton-right pb-5'>
            <button
              className={`button-third ${
                sizeWidth > 768 ? 'margin-right-2' : 'margin-bottom-1 mx-2'
              }`}
              type='button'
              onClick={handleCancelClick}
            >
              {`${t('CANCEL')}`}
            </button>
            <button className='button-first' type='submit'>
              {`${t('SAVE')}`}
            </button>
          </div>
        </Form>
      </div>
    </>
  )
}

const EditLinksForm = withFormik<EditLinksProps, EditLinksValues>({
  mapPropsToValues: (props:any) => {
    const { data } = props
    const initialValues: EditLinksValues = {
      _id: data._id,
      original_url: data.original_url,
      custom_code: data.custom_code,
      title: data.title,
      short_url: data.short_url,
      tags: data.tags,
      domain: data.domain,
      utm: {
        origin: data.utm?.origin || '',
        media: data.utm?.media || '',
        campaign: data.utm?.campaign || '',
        term: data.utm?.term || '',
        content: data.utm?.content || '',
      },
    }
    return { ...initialValues, ...props }
  },

  handleSubmit: (values: EditLinksValues, { props }:any) => {
    if (props.onSubmit) {
      props.onSubmit(values)
    }
  },
})(InnerForm)
export default EditLinksForm
